// Customer-facing assumptions rendered in the pricing sheet PDF. These are
// operational and scope assumptions only -- no rates, margins, or internal cost
// multipliers. The general block always shows; each module block is gated on
// whether that module is present in the proposal.
import { PRODUCT_DISPLAY_NAMES } from './pricingSheetData';

export interface AssumptionSection {
  heading: string;
  bullets: string[];
}

// Always shown, above the per-module blocks.
export const GENERAL_ASSUMPTIONS: string[] = [
  'Pricing is based on the volumes, configuration, and project duration stated in this proposal. Material changes to scope, volume, or duration will be re-quoted.',
  "Data captured under this agreement is securely retained in CRC Evans' cloud for 12 months from project completion. Extended retention can be arranged for an additional charge.",
  'DATA 360 platform access is provided for the duration specified in this proposal.',
  'Platform fees are based on the number of users and modules specified in this proposal.',
];

// Keyed by module id (matches priceBreakdown.products keys). Render order is
// fixed by MODULE_ASSUMPTION_ORDER below.
export const MODULE_ASSUMPTIONS: Record<string, string[]> = {
  welding: [
    'Hardware units are configured for the quoted number of welding systems and project duration. Changes to unit count or duration will be re-quoted.',
    'Connectivity mode (live vs. offline) is as specified in this proposal. Live mode requires on-site internet for real-time data sync; offline mode stores data on the device and syncs when connectivity becomes available.',
    'If the site has no internet during field operations, data remains on the device until connectivity is available; if internet is never available, no data is retained in the cloud.',
    'Training is delivered as the session type and quantity specified in this proposal.',
    'Spare units are provided as quoted; spare quantities and replacement terms are as agreed.',
    'Provisioning and on-field support are limited to the scope and quantities stated in this proposal.',
  ],
  materialTraceability: [
    'Pricing is based on the set of material documents per pipe identified during onboarding. A materially different document volume or mix will be re-quoted.',
    'Documents are processed primarily through automated extraction. Documents supplied in non-standard, handwritten, or poor-quality formats may require manual handling.',
    'The customer is responsible for providing complete and legible material documentation. Missing or illegible documents may require manual handling and could affect pricing.',
    'A one-time onboarding is performed per material source/mill to configure extraction, assuming representative sample documents are provided for each source.',
    'The service tier (extended processing vs. storage-only) is as specified in this proposal.',
  ],
  ndt: [
    'Pricing is based on the number of inspections and the report types per inspection specified in this proposal. A different inspection volume or report mix will be re-quoted.',
    'Inspection data is processed through automated analysis. Data supplied in non-standard or unsupported formats may require manual handling.',
    'DATA 360 offers two NDT service tiers. The Standard tier covers data processing and storage. The AI tier additionally applies automated AI analysis to the inspection data. The tier selected is as specified in this proposal.',
    'A one-time onboarding is performed per inspection vendor/data source, assuming representative sample data is provided.',
    'The customer is responsible for supplying inspection data in the agreed formats.',
  ],
  pipeFitter: [
    'Scanning is delivered either as a CRC Evans field service or on a bring-your-own-data (BYOD) basis, as specified in this proposal.',
    'For field scanning, pricing assumes continuous site access and standard pipe handling conditions. Significant site downtime or non-standard conditions may affect pricing.',
    'A one-time mobilization applies to field scanning services.',
    'For BYOD scanning, the customer provides scan data in the agreed format; a one-time onboarding is performed per data format.',
    'Reporting outputs (pipe data, pipe compatibility) are provided as configured in this proposal.',
    'OptiFit/OmniFit outputs are fit-up recommendations to support decision-making; final fit-up and welding decisions remain the customer\'s responsibility.',
  ],
  coating: [
    'Coating data is captured either on a bring-your-own-data (BYOD) basis or via CRC Evans bolt-on equipment installed on the coating line, as specified in this proposal.',
    'For BYOD, the customer provides coating records in the agreed format; a one-time onboarding is performed per contractor/data source, assuming representative sample data is provided.',
    'For bolt-on equipment, hardware is rented and remains the property of CRC Evans, configured for the quoted line(s) and project duration. Changes to equipment count or duration will be re-quoted. Data syncs when the site has internet; otherwise it is stored locally until connectivity is available.',
    'Coating records are processed through automated extraction; non-standard or poor-quality data may require manual handling.',
  ],
};

// Build the ordered list of assumption sections for a proposal: the general
// block first, then one block per included module that has assumption text.
// Blocks follow the order the modules appear in the proposal so they line up
// with the products table on the same sheet, using the same display names.
export function buildAssumptionSections(moduleIds: string[]): AssumptionSection[] {
  const sections: AssumptionSection[] = [
    { heading: 'General', bullets: GENERAL_ASSUMPTIONS },
  ];
  moduleIds.forEach(id => {
    const bullets = MODULE_ASSUMPTIONS[id];
    if (!bullets || bullets.length === 0) return;
    sections.push({ heading: PRODUCT_DISPLAY_NAMES[id] ?? id, bullets });
  });
  return sections;
}
